import React, { useState, useEffect, useMemo } from "react";
import { Line, Pie, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Tooltip,
  Legend,
} from "chart.js";
import { useNavigate, useLocation } from "react-router-dom";
import { FiArrowLeft } from "react-icons/fi";
import { searchUser } from "../../Service/apiService";
import "../../assets/css/Analytics.css";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css"; 

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Tooltip,
  Legend
);

const toLower = (val) => (val || "").toLowerCase().trim();

const parseDate = (str) => {
  if (!str) return null;
  const date = new Date(str);
  return isNaN(date) ? null : date;
};

export default function UsersAnalytics() {
  const navigate = useNavigate();
  const location = useLocation();

  const [timeRange, setTimeRange] = useState("1y");
  const [selectedCategory, setSelectedCategory] = useState("total");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  // For custom date calendar
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");

  // Fetch users
  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      try {
        const response = await searchUser("?userDeleted=false", {
          industry: "",
        });
        const raw = response?.result || [];
        setUsers(raw);
      } catch (err) {
        console.error("Error fetching users:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, []);

  // Compute date range
  const { computedStartDate, computedEndDate } = useMemo(() => {
    const now = new Date();
    if (timeRange === "custom") {
      const start = customStartDate
        ? new Date(customStartDate)
        : new Date(now.getFullYear() - 1, now.getMonth(), 1);
      const end = customEndDate ? new Date(customEndDate) : now;
      return { computedStartDate: start, computedEndDate: end };
    } else {
      let start;
      switch (timeRange) {
        case "1w":
          start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          break;
        case "1m":
          start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
          break;
        case "3m":
          start = new Date(now.getFullYear(), now.getMonth() - 3, 1);
          break;
        case "6m":
          start = new Date(now.getFullYear(), now.getMonth() - 6, 1);
          break;
        case "1y":
        default:
          start = new Date(now.getFullYear() - 1, now.getMonth(), 1);
      }
      return { computedStartDate: start, computedEndDate: now };
    }
  }, [timeRange, customStartDate, customEndDate]);

  // Categorize users
  const categorized = useMemo(() => {
    if (users.length === 0) return {};

    const total = users.filter((u) => toLower(u.companyType) !== "internal");
    const newUsers = users.filter(
      (u) =>
        toLower(u.companyType) === "new" &&
        u.invoiceDate &&
        u.activeStatus === true
    );
    const renewed = users.filter(
      (u) =>
        toLower(u.companyType) === "renewal" &&
        toLower(u.paymentStatus) === "paid" &&
        u.activeStatus === true
    );
    const lost = users.filter((u) => toLower(u.companyType) === "lost");
    const active = users.filter(
      (u) =>
        u.invoiceDate &&
        u.activeStatus === true &&
        toLower(u.companyType) !== "lost" &&
        toLower(u.companyType) !== "internal"
    );
    const unpaid = users.filter(
      (u) =>
        u.activeStatus === true &&
        toLower(u.paymentStatus) === "unpaid" &&
        toLower(u.companyType) !== "internal"
    );
    const internal = users.filter((u) => toLower(u.companyType) === "internal");

    return { total, new: newUsers, renewed, lost, active, unpaid, internal };
  }, [users]);

  // Color mapping
  const COLORS = {
    total: "rgba(2, 37, 68, 1)",
    new: "rgb(42, 86, 130)",
    renewed: "rgb(66, 111, 155)",
    lost: "rgba(93, 114, 135, 1)",
    active: "rgba(62, 106, 150, 1)",
    unpaid: "rgba(168, 172, 177, 1)",
    internal: "rgba(147, 193, 239, 1)",
  };

  // Pie chart data (without "Total")
  const pieData = useMemo(() => {
    const labels = ["New", "Renewed", "Lost", "Active", "Unpaid", "Internal"];
    const keyMap = {
      New: "new",
      Renewed: "renewed",
      Lost: "lost",
      Active: "active",
      Unpaid: "unpaid",
      Internal: "internal",
    };
    const data = labels.map((label) => categorized[keyMap[label]]?.length || 0);
    const backgroundColors = labels.map((label) => COLORS[keyMap[label]]);

    return {
      labels,
      datasets: [
        {
          data,
          backgroundColor: backgroundColors,
          borderColor: "#fff",
          borderWidth: 1,
        },
      ],
    };
  }, [categorized]);

  // Get relevant date for time-series
 const getDateForCategory = (user, category) => {
  const { createdAt, invoiceDate, subEndDate } = user;
  switch (category) {
    case "new":       return parseDate(invoiceDate) || parseDate(createdAt);
    case "renewed":   return parseDate(invoiceDate) || parseDate(subEndDate); // renewal *start* date (invoice)
    case "lost":      return parseDate(subEndDate) || parseDate(createdAt);
    case "unpaid":    return parseDate(invoiceDate) || parseDate(createdAt);
    case "internal":  return parseDate(createdAt) || parseDate(invoiceDate);
    case "total":
    case "active":
    default:          return parseDate(invoiceDate) || parseDate(createdAt);
  }
};
  // Time-series chart data
  // Time-series chart data
const timeChartData = useMemo(() => {
  if (!categorized[selectedCategory]?.length) {
    return { labels: [], datasets: [] };
  }

  const now = computedEndDate;
  const months = [];
  let current = new Date(computedStartDate);
  current.setDate(1); // align to 1st

  while (current <= now) {
    months.push(new Date(current));
    current.setMonth(current.getMonth() + 1);
  }

  const monthLabels = months.map((m) =>
    m.toLocaleString("default", { month: "short", year: "numeric" })
  );

  let data;

  // 🔁 SPECIAL HANDLING FOR "active": it's a state, not an event
  if (selectedCategory === "active") {
    // For each month, count users active *during* that month
    data = months.map((monthStart, i) => {
      const monthEnd = new Date(monthStart);
      monthEnd.setMonth(monthEnd.getMonth() + 1);
      monthEnd.setDate(0); // last day of month

      return users.filter((u) => {
        // Skip internal & lost (already filtered in categorized.active, but double-check)
        if (toLower(u.companyType) === "internal" || toLower(u.companyType) === "lost") 
          return false;

        // Must be marked active
        if (u.activeStatus !== true) return false;

        // Now check: was this user active during [monthStart, monthEnd]?
        // We need subscription/validity period — assume `subStartDate` and `subEndDate`
        const subStart = parseDate(u.subStartDate) || parseDate(u.invoiceDate);
        const subEnd = parseDate(u.subEndDate) || new Date(8640000000000000); // distant future if no end

        // Edge: if no subStart, fallback to createdAt or invoiceDate
        if (!subStart) {
          const fallbackDate = parseDate(u.invoiceDate) || parseDate(u.createdAt);
          if (!fallbackDate) return false;
          // assume active from fallbackDate onward (no end)
          return fallbackDate <= monthEnd;
        }

        // Overlap condition: [subStart, subEnd] ∩ [monthStart, monthEnd] ≠ ∅
        return subStart <= monthEnd && subEnd >= monthStart;
      }).length;
    });
  } 
  // 📈 For "total": cumulative join count (keep existing logic but improve)
  else if (selectedCategory === "total") {
    const joinDates = users
      .filter((u) => toLower(u.companyType) !== "internal")
      .map((u) => parseDate(u.invoiceDate) || parseDate(u.createdAt))
      .filter((d) => d !== null && d <= now); // only consider joins before now

    data = months.map((monthStart, i) => {
      const monthEnd = new Date(monthStart);
      monthEnd.setMonth(monthEnd.getMonth() + 1);
      monthEnd.setDate(0);
      return joinDates.filter((joinDate) => joinDate <= monthEnd).length;
    });
  } 
  // 📊 For event-based categories (new, renewed, lost, etc.)
  else {
    const monthlyMap = {};
    const usersInCategory = categorized[selectedCategory];
    usersInCategory.forEach((u) => {
      const date = getDateForCategory(u, selectedCategory);
      if (!date || date < computedStartDate || date > now) return;

      const monthKey = date.toLocaleString("default", {
        month: "short",
        year: "numeric",
      });
      monthlyMap[monthKey] = (monthlyMap[monthKey] || 0) + 1;
    });
    data = monthLabels.map((label) => monthlyMap[label] || 0);
  }

  const labelMap = {
    total: "Total Users (Cumulative)",
    new: "New Users",
    renewed: "Renewed Users",
    lost: "Lost Users",
    active: "Active Users (Monthly)",
    unpaid: "Unpaid Users",
    internal: "Internal Users",
  };

  return {
    labels: monthLabels,
    datasets: [
      {
        label: labelMap[selectedCategory] || "Users",
        data,
        borderColor: COLORS[selectedCategory],
        backgroundColor:
          selectedCategory === "total" || selectedCategory === "active"
            ? `${COLORS[selectedCategory]}20`
            : COLORS[selectedCategory],
        tension: 0.3,
        pointRadius: 3,
        pointBackgroundColor: COLORS[selectedCategory],
        pointBorderColor: "#fff",
        pointBorderWidth: 1,
        // Ensure bar for non-active/non-total
        type: selectedCategory === "total" || selectedCategory === "active" ? "line" : "bar",
      },
    ],
  };
}, [users, categorized, selectedCategory, computedStartDate, computedEndDate]);

  // Handle pie slice click
  const handlePieClick = (elements) => {
    if (elements.length > 0) {
      const index = elements[0].index;
      const keys = ["new", "renewed", "lost", "active", "unpaid", "internal"];
      setSelectedCategory(keys[index]);
    }
  };

  // Handle back navigation
  const handleBack = () => {
    const dashboardPath = location.pathname.replace("/analytics", "/dashboard");
    navigate(dashboardPath);
  };

  // Time range options
  const timeOptions = [
    { label: "1 Week", value: "1w" },
    { label: "1 Month", value: "1m" },
    { label: "3 Months", value: "3m" },
    { label: "6 Months", value: "6m" },
    { label: "1 Year", value: "1y" },
    { label: "Custom", value: "custom" },
  ];

  // Chart options
  const pieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
        labels: { font: { size: 11 }, color: "#333" },
      },
      tooltip: {
        backgroundColor: "#fff",
        titleColor: "rgb(5, 55, 99)",
        bodyColor: "#333",
        borderColor: "#eee",
        borderWidth: 1,
        padding: 10,
        borderRadius: 6,
      },
    },
    onClick: (_evt, elements) => handlePieClick(elements),
    onHover: (evt, elements) => {
      evt.native.target.style.cursor =
        elements.length > 0 ? "pointer" : "default";
    },
  };

  const lineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { labels: { font: { size: 11 }, color: "#333" } },
      tooltip: {
        backgroundColor: "#fff",
        titleColor: "rgb(5, 55, 99)",
        bodyColor: "#333",
        borderColor: "#eee",
        borderWidth: 1,
        padding: 10,
        borderRadius: 6,
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: "Month",
          color: "#666",
          font: { size: 12, weight: "bold" },
        },
        grid: { color: "rgba(0,0,0,0.04)" },
        ticks: { color: "#666", font: { size: 11 } },
      },
      y: {
        title: {
          display: true,
          text: "Number of Users",
          color: "#666",
          font: { size: 12, weight: "bold" },
        },
        beginAtZero: true,
        grid: { color: "rgba(0,0,0,0.04)" },
        ticks: { color: "#666", font: { size: 11 }, precision: 0 },
      },
    },
  };

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { labels: { font: { size: 11 }, color: "rgb(5, 55, 99)" } },
      tooltip: {
        backgroundColor: "#fff",
        titleColor: "rgb(5, 55, 99)",
        bodyColor: "rgb(5, 55, 99)",
        borderColor: "#eee",
        borderWidth: 1,
        padding: 10,
        borderRadius: 6,
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: "Month",
          color: "#666",
          font: { size: 12, weight: "bold" },
        },
        grid: { color: "rgba(0,0,0,0.04)" },
        ticks: { color: "#666", font: { size: 11 } },
      },
      y: {
        title: {
          display: true,
          text: "Number of Users",
          color: "#666",
          font: { size: 12, weight: "bold" },
        },
        beginAtZero: true,
        grid: { color: "rgba(0,0,0,0.04)" },
        ticks: { color: "#666", font: { size: 11 }, precision: 0 },
      },
    },
  };

  const selectedCategoryLabel = useMemo(() => {
    const labelMap = {
      total: "Total Users",
      new: "New Users",
      renewed: "Renewed Users",
      lost: "Lost Users",
      active: "Active Users",
      unpaid: "Unpaid Users",
      internal: "Internal Users",
    };
    return labelMap[selectedCategory] || "Users";
  }, [selectedCategory]);

  const totalUserCount = categorized.total?.length || 0;

  //for skeleton
    const UserAnalyticsSkeleton = () => (
    <div className="analytics-content">
      {/* Pie chart skeleton */}
      <div className="chart-panel chart-panel--with-total">
        <Skeleton height={22} width={180} style={{ marginBottom: 12 }} />
        <Skeleton height={28} width={120} style={{ marginBottom: 16 }} />
        <Skeleton height={260} borderRadius={12} />
      </div>
  
      {/* Trend chart skeleton */}
      <div className="chart-panel">
        <Skeleton height={22} width={220} style={{ marginBottom: 16 }} />
        <Skeleton height={300} borderRadius={12} />
      </div>
    </div>
  );
  return (
    <div className="analytics-container">
      <div className="header-section">
      
        <h1 className="title">User Analytics</h1>
        <div className="time-filter-container">
          <select
            className="time-range-select"
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
          >
            {timeOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          {timeRange === "custom" && (
            <div className="custom-date-picker">
              <input
                type="date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                className="date-input"
              />
              <input
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                className="date-input"
              />
            </div>
          )}
        </div>
      </div>

      {loading ? (
        <UserAnalyticsSkeleton />
      ) : (
        <div className="analytics-content">
          {/* Pie Chart with Total Users Box */}
          <div className="chart-panel chart-panel--with-total">
            <h3>User Distribution</h3>
            <div
              className="total-users-box"
              onClick={() => setSelectedCategory("total")}
            >
              Total: {totalUserCount.toLocaleString()}
            </div>
            <div className="chart-wrapper">
              <Pie data={pieData} options={pieOptions} />
            </div>
          </div>

          {/* Trend Line Chart */}
          <div className="chart-panel">
            <h3>Trend: {selectedCategoryLabel}</h3>
            <div className="chart-wrapper">
              {timeChartData.labels.length > 0 ? (
                selectedCategory === "total" ? (
                  <Line data={timeChartData} options={lineOptions} />
                ) : (
                  <Bar data={timeChartData} options={barOptions} />
                )
              ) : (
                <div className="no-data">No data in selected period</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
